import { useEffect, useState } from "react";

const LoadingScreen = ({ onComplete }) => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    // Stage 0: Premium Logo Motion & Pulse
    // Stage 1: Smooth Cinematic Curtain Unveil
    const t1 = setTimeout(() => setStage(1), 1100);
    // Stage 2: Clean DOM Unmount
    const t2 = setTimeout(() => {
      setStage(2);
      if (onComplete) onComplete();
    }, 1750);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onComplete]);

  if (stage === 2) return null;

  const isRevealing = stage === 1;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: isRevealing ? "none" : "auto",
        transition: "opacity 0.65s cubic-bezier(0.85, 0, 0.15, 1)",
        opacity: isRevealing ? 0 : 1,
      }}
      aria-hidden={isRevealing}
    >
      {/* Upper Curtain */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: "50%",
          backgroundColor: "#05070E",
          transition: "transform 0.75s cubic-bezier(0.85, 0, 0.15, 1)",
          transform: isRevealing ? "translateY(-100%)" : "translateY(0)",
          borderBottom: "1px solid rgba(0, 255, 136, 0.15)",
          boxShadow: isRevealing ? "0 20px 50px rgba(0, 0, 0, 0.9)" : "none",
        }}
      />

      {/* Lower Curtain */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#05070E",
          transition: "transform 0.75s cubic-bezier(0.85, 0, 0.15, 1)",
          transform: isRevealing ? "translateY(100%)" : "translateY(0)",
          borderTop: "1px solid rgba(0, 255, 136, 0.15)",
          boxShadow: isRevealing ? "0 -20px 50px rgba(0, 0, 0, 0.9)" : "none",
        }}
      />

      {/* Ambient Radial Aura Glow */}
      <div
        style={{
          position: "absolute",
          width: "480px",
          height: "480px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0, 255, 136, 0.22) 0%, rgba(0, 240, 255, 0.08) 45%, transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          transform: isRevealing ? "scale(1.4)" : "scale(1)",
          transition: "transform 0.75s cubic-bezier(0.85, 0, 0.15, 1), opacity 0.5s ease",
          opacity: isRevealing ? 0 : 1,
        }}
      />

      {/* Center Branded Motion Core */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
          transition: "transform 0.7s cubic-bezier(0.85, 0, 0.15, 1), opacity 0.4s ease",
          transform: isRevealing ? "scale(1.15) translateY(-10px)" : "scale(1) translateY(0)",
          opacity: isRevealing ? 0 : 1,
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Glowing Logo Card */}
        <div
          style={{
            position: "relative",
            width: "88px",
            height: "88px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Animated Neon Halo Border */}
          <div
            style={{
              position: "absolute",
              inset: "-6px",
              borderRadius: "26px",
              border: "1px solid rgba(0, 255, 136, 0.4)",
              boxShadow: "0 0 40px rgba(0, 255, 136, 0.28)",
              animation: "jamadhanGlow 2.4s ease-in-out infinite alternate",
            }}
          />
          <img
            src="/jamadhan-logo.png"
            alt="JamaDhan"
            style={{
              width: "76px",
              height: "76px",
              borderRadius: "20px",
              objectFit: "cover",
              boxShadow: "0 12px 32px rgba(0, 0, 0, 0.75)",
            }}
          />
        </div>

        {/* Brand Typography */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "6px",
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: "18px",
              fontWeight: 900,
              letterSpacing: "4.5px",
              color: "#FFFFFF",
              textTransform: "uppercase",
              background: "linear-gradient(135deg, #FFFFFF 0%, #F1F5F9 55%, #00FF88 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            JAMADHAN
          </span>
          <span
            style={{
              fontSize: "10.5px",
              fontWeight: 700,
              letterSpacing: "3px",
              color: "#A1A1AA",
              textTransform: "uppercase",
            }}
          >
            Institutional Trading
          </span>
        </div>

        {/* Shimmer Progress Track */}
        <div
          style={{
            width: "140px",
            height: "2.5px",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            borderRadius: "999px",
            overflow: "hidden",
            position: "relative",
            marginTop: "4px",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              width: "50%",
              background: "linear-gradient(90deg, transparent, #00FF88, #00F0FF, transparent)",
              borderRadius: "999px",
              animation: "jamadhanProgress 1.3s cubic-bezier(0.65, 0, 0.35, 1) infinite",
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes jamadhanGlow {
          0% {
            border-color: rgba(0, 255, 136, 0.3);
            box-shadow: 0 0 25px rgba(0, 255, 136, 0.2);
          }
          100% {
            border-color: rgba(0, 240, 255, 0.6);
            box-shadow: 0 0 50px rgba(0, 255, 136, 0.45);
          }
        }
        @keyframes jamadhanProgress {
          0% {
            left: -50%;
          }
          100% {
            left: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
